import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  PlayArrow as PlayArrowIcon,
  Save as SaveIcon,
} from '@mui/icons-material';

import { reportService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout/Layout';

const DATASET_OPTIONS = [
  { value: 'assets', label: 'Ativos' },
  { value: 'licenses', label: 'Licenças' },
  { value: 'certificates', label: 'Certificados' },
  { value: 'domains', label: 'Domínios' },
  { value: 'audit_logs', label: 'Auditoria' },
];

const DATASET_FIELDS = {
  assets: ['id', 'name', 'category', 'subcategory', 'owner', 'location', 'status', 'expiry_date', 'usage_term_file', 'created_at'],
  licenses: ['id', 'name', 'software_name', 'status', 'expiration_date', 'created_at'],
  certificates: ['id', 'name', 'dns_name', 'status', 'expiration_date', 'created_at'],
  domains: ['id', 'name', 'domain_name', 'status', 'is_active', 'expiration_date', 'created_at'],
  audit_logs: ['id', 'user_id', 'action', 'resource_type', 'resource_id', 'status', 'method', 'endpoint', 'created_at'],
};

const FILTER_OPERATORS = [
  { value: 'eq', label: '=' },
  { value: 'ne', label: '!=' },
  { value: 'contains', label: 'contém' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'is_null', label: 'vazio' },
  { value: 'not_null', label: 'preenchido' },
  { value: 'within_days', label: 'vence em X dias' },
];

const EMPTY_DRAFT = {
  name: '',
  description: '',
  is_shared: false,
  definition_json: {
    dataset: 'assets',
    columns: ['id', 'name', 'status'],
    filters: [],
    sort: [{ field: 'created_at', direction: 'desc' }],
    limit: 200,
  },
};

const SummaryCard = ({ title, value, subtitle }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ p: 2.2 }}>
      <Typography variant="h4" sx={{ lineHeight: 1.05 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.8, display: 'block' }}>
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const formatCellValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }

  return String(value);
};

const Reports = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [reports, setReports] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [executingLabel, setExecutingLabel] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fieldsForDraft = useMemo(
    () => DATASET_FIELDS[draft.definition_json.dataset] || [],
    [draft.definition_json.dataset]
  );

  const selectedDatasetLabel = useMemo(
    () => DATASET_OPTIONS.find((option) => option.value === draft.definition_json.dataset)?.label || draft.definition_json.dataset,
    [draft.definition_json.dataset]
  );

  const loadData = async () => {
    try {
      setLoading(true);
      const [templateResp, reportResp] = await Promise.all([
        reportService.getTemplates(),
        reportService.getReports(),
      ]);
      setTemplates(templateResp.templates || []);
      setReports(reportResp.reports || []);
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Erro ao carregar relatórios', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const runTemplate = async (templateCode) => {
    try {
      setExecutingLabel(`template-${templateCode}`);
      const response = await reportService.runReport({ template_code: templateCode });
      setResult({
        title: `Resultado do template: ${templateCode}`,
        ...response.result,
      });
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Erro ao executar template', severity: 'error' });
    } finally {
      setExecutingLabel('');
    }
  };

  const runSavedReport = async (reportId) => {
    try {
      setExecutingLabel(`report-${reportId}`);
      const response = await reportService.runReport({ report_id: reportId });
      setResult({
        title: `Resultado do relatório #${reportId}`,
        ...response.result,
      });
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Erro ao executar relatório', severity: 'error' });
    } finally {
      setExecutingLabel('');
    }
  };

  const runDraftPreview = async () => {
    try {
      setExecutingLabel('preview');
      const response = await reportService.runReport({ definition: draft.definition_json });
      setResult({
        title: `Prévia: ${draft.name || 'Relatório sem nome'}`,
        ...response.result,
      });
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Erro na prévia', severity: 'error' });
    } finally {
      setExecutingLabel('');
    }
  };

  const saveDraft = async () => {
    try {
      if (!draft.name.trim()) {
        setSnackbar({ open: true, message: 'Informe o nome do relatório', severity: 'warning' });
        return;
      }

      await reportService.createReport(draft);
      setBuilderOpen(false);
      setDraft(EMPTY_DRAFT);
      await loadData();
      setSnackbar({ open: true, message: 'Relatório salvo com sucesso', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Erro ao salvar relatório', severity: 'error' });
    }
  };

  const removeReport = async (id) => {
    try {
      await reportService.deleteReport(id);
      await loadData();
      setSnackbar({ open: true, message: 'Relatório removido', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Erro ao remover relatório', severity: 'error' });
    }
  };

  const exportReport = async (id) => {
    try {
      await reportService.exportReport(id);
      setSnackbar({ open: true, message: 'Exportação iniciada', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Erro ao exportar relatório', severity: 'error' });
    }
  };

  const addFilter = () => {
    setDraft((prev) => ({
      ...prev,
      definition_json: {
        ...prev.definition_json,
        filters: [...prev.definition_json.filters, { field: '', operator: 'eq', value: '' }],
      },
    }));
  };

  const updateFilter = (index, patch) => {
    setDraft((prev) => {
      const nextFilters = [...prev.definition_json.filters];
      nextFilters[index] = { ...nextFilters[index], ...patch };
      return {
        ...prev,
        definition_json: {
          ...prev.definition_json,
          filters: nextFilters,
        },
      };
    });
  };

  const removeFilter = (index) => {
    setDraft((prev) => ({
      ...prev,
      definition_json: {
        ...prev.definition_json,
        filters: prev.definition_json.filters.filter((_, currentIndex) => currentIndex !== index),
      },
    }));
  };

  const resultColumns = result?.columns || [];
  const resultRows = result?.rows || [];

  if (user?.role !== 'admin' && user?.role !== 'manager') {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">Acesso restrito a administradores e gestores.</Alert>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, maxWidth: 1280, mx: 'auto', width: '100%' }}>
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            mb: 2.5,
            border: 'none',
            color: 'common.white',
            background: 'linear-gradient(130deg, #1f5eff 0%, #00a387 100%)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h4" sx={{ mb: 0.6 }}>
                Relatórios
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.95 }}>
                Relatórios prontos de auditoria e builder para consultas customizadas.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setBuilderOpen(true)}
              sx={{ bgcolor: 'rgba(255,255,255,0.16)', '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' } }}
            >
              Criar relatório
            </Button>
          </Stack>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={4}>
            <SummaryCard title="Templates disponíveis" value={templates.length} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SummaryCard title="Relatórios salvos" value={reports.length} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SummaryCard
              title="Última execução"
              value={result?.count ?? '-'}
              subtitle={result ? `${result.dataset} retornou ${result.count} registros` : 'Execute um relatório para visualizar'}
            />
          </Grid>
        </Grid>

        {loading && (
          <Paper sx={{ p: 2, mb: 2.5 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Carregando relatórios...
            </Typography>
            <LinearProgress />
          </Paper>
        )}

        <Paper sx={{ p: 2.2, mb: 2.2 }}>
          <Typography variant="h6" sx={{ mb: 1.2 }}>
            Templates críticos
          </Typography>
          <Grid container spacing={1.5}>
            {templates.map((template) => (
              <Grid item xs={12} md={6} key={template.code}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Stack spacing={1.2}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                          {template.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {template.description}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Chip size="small" label={template.code} variant="outlined" />
                        <Button
                          size="small"
                          startIcon={<PlayArrowIcon />}
                          onClick={() => runTemplate(template.code)}
                          disabled={executingLabel === `template-${template.code}`}
                        >
                          Executar
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {!loading && templates.length === 0 && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              Nenhum template disponível.
            </Alert>
          )}
        </Paper>

        <Paper sx={{ p: 2.2, mb: 2.2 }}>
          <Typography variant="h6" sx={{ mb: 1.2 }}>
            Relatórios salvos
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Dataset</TableCell>
                  <TableCell>Criado em</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={0.8} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {row.name}
                        </Typography>
                        {row.is_shared && <Chip label="Compartilhado" size="small" color="secondary" variant="outlined" />}
                      </Stack>
                    </TableCell>
                    <TableCell>{row.definition_json?.dataset}</TableCell>
                    <TableCell>{new Date(row.created_at).toLocaleString('pt-BR')}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Executar">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => runSavedReport(row.id)}
                            disabled={executingLabel === `report-${row.id}`}
                          >
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Exportar">
                        <IconButton size="small" onClick={() => exportReport(row.id)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => removeReport(row.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && reports.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Nenhum relatório salvo.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {result && (
          <Paper sx={{ p: 2.2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1.2 }}>
              <Box>
                <Typography variant="h6">{result.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Dataset: {result.dataset} | Registros: {result.count}
                </Typography>
              </Box>
              <Chip size="small" label={`${result.count} linhas`} color="primary" variant="outlined" />
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {resultColumns.map((column) => (
                      <TableCell key={column}>{column}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultRows.map((row, index) => (
                    <TableRow key={`${index}-${row.id || 'row'}`} hover>
                      {resultColumns.map((column) => (
                        <TableCell key={`${index}-${column}`}>{formatCellValue(row[column])}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {resultRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={resultColumns.length || 1} align="center">
                        Sem resultados.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <Dialog open={builderOpen} onClose={() => setBuilderOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>Novo relatório customizado</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Nome"
                  value={draft.name}
                  onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Dataset</InputLabel>
                  <Select
                    value={draft.definition_json.dataset}
                    label="Dataset"
                    onChange={(event) => {
                      const dataset = event.target.value;
                      setDraft((prev) => ({
                        ...prev,
                        definition_json: {
                          ...prev.definition_json,
                          dataset,
                          columns: (DATASET_FIELDS[dataset] || []).slice(0, 4),
                          filters: [],
                        },
                      }));
                    }}
                  >
                    {DATASET_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descrição"
                  value={draft.description}
                  onChange={(event) => setDraft((prev) => ({ ...prev, description: event.target.value }))}
                />
              </Grid>

              <Grid item xs={12} md={9}>
                <FormControl fullWidth>
                  <InputLabel>Colunas</InputLabel>
                  <Select
                    multiple
                    label="Colunas"
                    value={draft.definition_json.columns}
                    renderValue={(selected) => (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {selected.map((column) => (
                          <Chip key={column} label={column} size="small" />
                        ))}
                      </Stack>
                    )}
                    onChange={(event) => {
                      const nextColumns = typeof event.target.value === 'string'
                        ? event.target.value.split(',')
                        : event.target.value;

                      setDraft((prev) => ({
                        ...prev,
                        definition_json: {
                          ...prev.definition_json,
                          columns: nextColumns,
                        },
                      }));
                    }}
                  >
                    {fieldsForDraft.map((field) => (
                      <MenuItem key={field} value={field}>
                        {field}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  type="number"
                  fullWidth
                  label="Limite"
                  value={draft.definition_json.limit}
                  onChange={(event) => {
                    const limit = Number(event.target.value || 200);
                    setDraft((prev) => ({
                      ...prev,
                      definition_json: {
                        ...prev.definition_json,
                        limit,
                      },
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!draft.is_shared}
                      onChange={(event) => setDraft((prev) => ({ ...prev, is_shared: event.target.checked }))}
                    />
                  }
                  label="Compartilhar relatório com outros usuários"
                />
              </Grid>

              <Grid item xs={12}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">Filtros</Typography>
                  <Chip size="small" label={`Dataset atual: ${selectedDatasetLabel}`} variant="outlined" />
                </Stack>

                {draft.definition_json.filters.map((filter, index) => (
                  <Paper key={`filter-${index}`} variant="outlined" sx={{ p: 1.2, mb: 1 }}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Campo</InputLabel>
                          <Select
                            value={filter.field}
                            label="Campo"
                            onChange={(event) => updateFilter(index, { field: event.target.value })}
                          >
                            {fieldsForDraft.map((field) => (
                              <MenuItem key={field} value={field}>
                                {field}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Operador</InputLabel>
                          <Select
                            value={filter.operator}
                            label="Operador"
                            onChange={(event) => updateFilter(index, { operator: event.target.value })}
                          >
                            {FILTER_OPERATORS.map((operator) => (
                              <MenuItem key={operator.value} value={operator.value}>
                                {operator.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Valor"
                          value={filter.value}
                          onChange={(event) => updateFilter(index, { value: event.target.value })}
                          disabled={filter.operator === 'is_null' || filter.operator === 'not_null'}
                        />
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <IconButton onClick={() => removeFilter(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}

                <Button size="small" onClick={addFilter}>
                  Adicionar filtro
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBuilderOpen(false)}>Cancelar</Button>
            <Button onClick={runDraftPreview} startIcon={<PlayArrowIcon />} disabled={executingLabel === 'preview'}>
              Prévia
            </Button>
            <Button variant="contained" onClick={saveDraft} startIcon={<SaveIcon />}>
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default Reports;
